/**
 * PRD-49 — which (language, exercise type) pairs the `codigo` request
 * surface may offer.
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │ SINGLE SOURCE OF TRUTH — THE ENGINE, NOT THIS FILE.                 │
 * │                                                                     │
 * │   platonica-assignment-engine                                       │
 * │     src/platonica_alpha/web/spec_loader.py                          │
 * │       _COMPATIBLE_EXERCISE_TYPES_BY_LANGUAGE                        │
 * │                                                                     │
 * │ The map below is a MIRROR of that table. The manifest does not      │
 * │ publish per-language compatibility — `inputs.language.choices` is a │
 * │ flat list of codes+labels and `inputs.item_type.choices` is a flat  │
 * │ list of exercise codes — so there is nothing on the wire to derive  │
 * │ this from. Until the manifest grows a `compatible_item_types` field │
 * │ per language choice, a mirror is the only option, and the mirror is │
 * │ the risk.                                                           │
 * │                                                                     │
 * │ WHAT DRIFT COSTS: the engine fails CLOSED. A pair the engine does   │
 * │ not accept is rejected with `item_type_language_incompatible` — a   │
 * │ terminal failure for EVERY student in the classroom, with the       │
 * │ material pinned in PROCESSING and no action the professor can take. │
 * │ Widening this map before the engine widens its own is how you ship  │
 * │ that. Narrowing it merely hides a working combination.              │
 * │                                                                     │
 * │ ORDERING: when the engine's table changes, this file changes in the │
 * │ SAME direction and the engine ships FIRST — its change only widens  │
 * │ what is accepted, so it is a no-op while the SPA still sends the    │
 * │ old pairs. The reverse order is the classroom-wide failure above.   │
 * └─────────────────────────────────────────────────────────────────────┘
 *
 * `pseudocode` is the one language with a genuinely smaller set, and it
 * is PERMANENT, not a rollout stage: `predict_output` / `debug_code` /
 * `complete_code` all start from a program that actually runs, and the
 * engine's Spanish dialect has no compiler or toolchain behind it. Never
 * describe those three as "coming soon" — they are not coming.
 *
 * `html_css` (PRD-72) is the other sentinel, and its gap is permanent too:
 * a page is drawn by the browser, it does not print an output, so there is
 * nothing to predict. Build, complete and debug all apply — the engine
 * grades the page with static HTML/CSS checks instead of running it.
 *
 * `c` is absent on purpose. The manifest declares it, but the
 * native_compiled sandbox's `Capabilities()` returns exactly
 * {cpp:17, cpp:20} and refuses a `c:17` request before anything runs
 * (see infra/terraform/variables.tf::native_compiled). A key here would
 * put it on screen as pickable.
 *
 * Everything else in this module is DERIVED from the map — the enabled
 * language set, the per-language option list, the validity predicate.
 * That mirrors how the engine derives `_GENERATION_ENABLED_LANGUAGES`
 * from its own table rather than typing a second list, so the two halves
 * of one fact cannot fall out of sync inside this file.
 *
 * The backend and the engine validate independently
 * (`language_invalid`, `language_not_supported_for_type`,
 * `item_type_language_incompatible`), so this constant is UX gating,
 * never the security boundary.
 */

import type { CodingItemType, ProgrammingLanguage } from "@/types"

const COMPATIBLE_EXERCISE_TYPES_BY_LANGUAGE = {
  // No compiler, no runtime: only the two formats that ask the student to
  // WRITE code rather than reason about code that already runs.
  pseudocode: ["implement_function", "mixed"],
  python: ["implement_function", "predict_output", "debug_code", "complete_code", "mixed"],
  javascript: ["implement_function", "predict_output", "debug_code", "complete_code", "mixed"],
  java: ["implement_function", "predict_output", "debug_code", "complete_code", "mixed"],
  cpp: ["implement_function", "predict_output", "debug_code", "complete_code", "mixed"],
  // A page has no output to predict; every other format applies.
  html_css: ["implement_function", "debug_code", "complete_code", "mixed"],
} as const satisfies Partial<Record<ProgrammingLanguage, readonly CodingItemType[]>>

type EnabledProgrammingLanguage = keyof typeof COMPATIBLE_EXERCISE_TYPES_BY_LANGUAGE

/**
 * The languages the professor may pick — DERIVED from the map's keys, the
 * way the engine derives `_GENERATION_ENABLED_LANGUAGES`. There is no
 * second list to update.
 *
 * This set is membership only; the ORDER the selector renders comes from
 * the manifest (`inputs.language.choices`), so adding a language here can
 * never reorder the segmented control behind the professor's back.
 */
export const PROGRAMMING_ENABLED_LANGUAGES = Object.keys(
  COMPATIBLE_EXERCISE_TYPES_BY_LANGUAGE,
) as readonly EnabledProgrammingLanguage[]

/**
 * The markup sentinel (PRD-72). Exported so a surface that words a language's
 * limits can tell "nothing runs here" (pseudocode) from "there is no output to
 * predict" (HTML and CSS) — the two gaps have different reasons.
 */
export const MARKUP_LANGUAGE = "html_css" satisfies ProgrammingLanguage

/** Every enabled language supports this one, which makes it the safe reset target. */
const UNIVERSAL_EXERCISE_TYPE: CodingItemType = "implement_function"

export const isProgrammingLanguageEnabled = (
  language: string,
): language is EnabledProgrammingLanguage =>
  // Membership through the derived array rather than `language in map`:
  // `in` walks the prototype chain, so "toString" would answer true.
  (PROGRAMMING_ENABLED_LANGUAGES as readonly string[]).includes(language)

/** The exercise types a given language accepts; empty for a language we don't offer. */
export const exerciseTypesForLanguage = (language: string): readonly CodingItemType[] =>
  isProgrammingLanguageEnabled(language) ? COMPATIBLE_EXERCISE_TYPES_BY_LANGUAGE[language] : []

/**
 * The PAIR predicate. Both halves have to be checked together — each one
 * alone is individually valid for combinations the engine rejects
 * (`pseudocode` is fine, `debug_code` is fine, the two together are a
 * terminal `item_type_language_incompatible`).
 */
export const isProgrammingComboSupported = (language: string, itemType: string): boolean =>
  (exerciseTypesForLanguage(language) as readonly string[]).includes(itemType)

/**
 * Where the exercise-type selector lands when the current pick stops
 * being valid for the newly-chosen language. `implement_function` for
 * every language we offer; the first compatible type is a defensive
 * fallback if that ever stops being true on the engine side.
 */
export const defaultExerciseTypeForLanguage = (language: string): CodingItemType => {
  const compatible = exerciseTypesForLanguage(language)
  return compatible.includes(UNIVERSAL_EXERCISE_TYPE)
    ? UNIVERSAL_EXERCISE_TYPE
    : (compatible[0] ?? UNIVERSAL_EXERCISE_TYPE)
}
